import { z } from 'zod';

const identifierSchema = z.string().min(1).max(200);
const versionSchema = z.number().int().nonnegative();
const canonicalContentHashSchema = z.string().regex(/^sha256-jcs-v1:[a-f0-9]{64}$/u);

export const actionDraftLifecycleSchema = z.enum(['active', 'cancelled', 'terminal']);
export const actionDraftRevisionLifecycleSchema = z.enum([
  'editing',
  'submitted',
  'superseded',
  'cancelled',
]);

export const actionDraftAuthoritySchema = z
  .object({
    workspaceId: identifierSchema,
    actorUserId: identifierSchema,
    actorMemberId: identifierSchema,
    conversationId: identifierSchema,
  })
  .strict();

export const scopedActionDraftIdSchema = actionDraftAuthoritySchema
  .pick({ workspaceId: true, conversationId: true })
  .extend({ actionDraftId: identifierSchema })
  .strict();

export const actionDraftRevisionSnapshotSchema = z
  .object({
    schemaVersion: z.literal(1),
    gatewayOperation: identifierSchema,
    payload: z.record(z.unknown()),
    contentHash: canonicalContentHashSchema,
  })
  .strict();

export const actionDraftSchema = scopedActionDraftIdSchema
  .extend({
    currentRevisionId: identifierSchema.nullable(),
    lifecycle: actionDraftLifecycleSchema,
    stateVersion: versionSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

export const actionDraftRevisionSchema = scopedActionDraftIdSchema
  .extend({
    actionDraftRevisionId: identifierSchema,
    revisionNumber: z.number().int().positive(),
    lifecycle: actionDraftRevisionLifecycleSchema,
    snapshot: actionDraftRevisionSnapshotSchema,
    approvalRequestId: identifierSchema.nullable(),
    toolExecutionIds: z.array(identifierSchema).readonly(),
    createdBy: actionDraftAuthoritySchema,
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((revision, context) => {
    if (revision.workspaceId !== revision.createdBy.workspaceId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['createdBy', 'workspaceId'],
        message: 'createdBy workspace must match revision workspace',
      });
    }
    if (revision.conversationId !== revision.createdBy.conversationId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['createdBy', 'conversationId'],
        message: 'createdBy conversation must match revision conversation',
      });
    }
    if (
      revision.lifecycle === 'editing' &&
      (revision.approvalRequestId !== null || revision.toolExecutionIds.length > 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['approvalRequestId'],
        message: 'editing revision cannot expose gateway projections',
      });
    }
    if (new Set(revision.toolExecutionIds).size !== revision.toolExecutionIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toolExecutionIds'],
        message: 'revision tool execution links must be unique',
      });
    }
  });

const idempotencySchema = z
  .object({
    operation: z.enum(['create', 'revise', 'read', 'submit', 'cancel']),
    idempotencyKey: z.string().min(16).max(200),
  })
  .strict();

export const createActionDraftCommandSchema = actionDraftAuthoritySchema
  .extend({
    command: z.literal('create'),
    idempotency: idempotencySchema.extend({ operation: z.literal('create') }),
    snapshot: actionDraftRevisionSnapshotSchema,
  })
  .strict();

export const reviseActionDraftCommandSchema = actionDraftAuthoritySchema
  .extend({
    command: z.literal('revise'),
    actionDraftId: identifierSchema,
    expectedDraftVersion: versionSchema,
    expectedCurrentRevisionId: identifierSchema,
    idempotency: idempotencySchema.extend({ operation: z.literal('revise') }),
    snapshot: actionDraftRevisionSnapshotSchema,
  })
  .strict();

export const readActionDraftCommandSchema = actionDraftAuthoritySchema
  .extend({
    command: z.literal('read'),
    actionDraftId: identifierSchema,
    idempotency: idempotencySchema.extend({ operation: z.literal('read') }),
  })
  .strict();

export const submitActionDraftCommandSchema = actionDraftAuthoritySchema
  .extend({
    command: z.literal('submit'),
    actionDraftId: identifierSchema,
    actionDraftRevisionId: identifierSchema,
    expectedDraftVersion: versionSchema,
    expectedCurrentRevisionId: identifierSchema,
    expectedRevisionLifecycle: z.literal('editing'),
    idempotency: idempotencySchema.extend({ operation: z.literal('submit') }),
  })
  .strict();

export const cancelActionDraftCommandSchema = actionDraftAuthoritySchema
  .extend({
    command: z.literal('cancel'),
    actionDraftId: identifierSchema,
    expectedDraftVersion: versionSchema,
    idempotency: idempotencySchema.extend({ operation: z.literal('cancel') }),
  })
  .strict();

export const actionDraftCommandSchema = z.discriminatedUnion('command', [
  createActionDraftCommandSchema,
  reviseActionDraftCommandSchema,
  readActionDraftCommandSchema,
  submitActionDraftCommandSchema,
  cancelActionDraftCommandSchema,
]);

export const actionDraftErrorCodeSchema = z.enum([
  'ACTION_DRAFT_NOT_FOUND',
  'ACTION_DRAFT_SCOPE_MISMATCH',
  'ACTION_DRAFT_AUTHORITY_REVOKED',
  'ACTION_DRAFT_VERSION_CONFLICT',
  'ACTION_DRAFT_REVISION_IMMUTABLE',
  'ACTION_DRAFT_REVISION_NOT_CURRENT',
  'ACTION_DRAFT_IDEMPOTENCY_CONFLICT',
  'ACTION_DRAFT_TRANSPORT_BINDING_MISMATCH',
]);

/**
 * A Telegram action is valid only when every durable binding is replayed.
 * The transport actor is deliberately distinct from the CRM actor authority.
 */
export const telegramActionDraftCallbackSchema = z
  .object({
    workspaceId: identifierSchema,
    conversationId: identifierSchema,
    actionDraftId: identifierSchema,
    actionDraftRevisionId: identifierSchema,
    approvalRequestId: identifierSchema,
    callbackId: identifierSchema,
    idempotencyKey: z.string().min(16).max(200),
    payloadFingerprint: canonicalContentHashSchema,
    transportAuthority: z
      .object({
        provider: z.literal('telegram'),
        chatBindingId: identifierSchema,
        actorBindingId: identifierSchema,
        telegramUserId: identifierSchema,
      })
      .strict(),
  })
  .strict();

export const actionDraftConcurrencyStateSchema = scopedActionDraftIdSchema
  .extend({
    stateVersion: versionSchema,
    currentRevisionId: identifierSchema,
  })
  .strict();

export const actionDraftIdempotencyRecordSchema = z
  .object({
    workspaceId: identifierSchema,
    conversationId: identifierSchema,
    actorMemberId: identifierSchema,
    operation: z.enum(['create', 'revise', 'read', 'submit', 'cancel']),
    idempotencyKey: z.string().min(16).max(200),
    payloadFingerprint: canonicalContentHashSchema,
  })
  .strict();

export const actionDraftGatewayProjectionSchema = z
  .object({
    revision: actionDraftRevisionSchema,
    approvalRequest: z
      .object({
        id: identifierSchema,
        workspaceId: identifierSchema,
        conversationId: identifierSchema,
      })
      .strict(),
    toolExecutions: z
      .array(
        z
          .object({
            id: identifierSchema,
            workspaceId: identifierSchema,
            conversationId: identifierSchema,
            approvalRequestId: identifierSchema,
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export type ContractValidationResult =
  | { readonly success: true }
  | { readonly success: false; readonly errorCode: ActionDraftErrorCode };

/**
 * Compares already Zod-parsed, server-owned concurrency values; it does not
 * read persistence or authorize the caller.
 */
export function validateActionDraftCommandCas(
  command:
    | z.infer<typeof reviseActionDraftCommandSchema>
    | z.infer<typeof submitActionDraftCommandSchema>,
  current: z.infer<typeof actionDraftConcurrencyStateSchema>,
): ContractValidationResult {
  if (
    command.workspaceId !== current.workspaceId ||
    command.conversationId !== current.conversationId ||
    command.actionDraftId !== current.actionDraftId
  ) {
    return { success: false, errorCode: 'ACTION_DRAFT_SCOPE_MISMATCH' };
  }
  if (
    command.expectedDraftVersion !== current.stateVersion ||
    command.expectedCurrentRevisionId !== current.currentRevisionId
  ) {
    return { success: false, errorCode: 'ACTION_DRAFT_VERSION_CONFLICT' };
  }
  return { success: true };
}

/** Compares already Zod-parsed idempotency records in their operation scope. */
export function validateActionDraftIdempotency(
  replay: z.infer<typeof actionDraftIdempotencyRecordSchema>,
  existing: z.infer<typeof actionDraftIdempotencyRecordSchema>,
): ContractValidationResult {
  const sameScopeAndKey =
    replay.workspaceId === existing.workspaceId &&
    replay.conversationId === existing.conversationId &&
    replay.actorMemberId === existing.actorMemberId &&
    replay.operation === existing.operation &&
    replay.idempotencyKey === existing.idempotencyKey;
  return !sameScopeAndKey || replay.payloadFingerprint === existing.payloadFingerprint
    ? { success: true }
    : { success: false, errorCode: 'ACTION_DRAFT_IDEMPOTENCY_CONFLICT' };
}

/** Validates already Zod-parsed gateway projections, not raw persistence data. */
export function validateActionDraftGatewayProjection(
  projection: z.infer<typeof actionDraftGatewayProjectionSchema>,
): ContractValidationResult {
  const { approvalRequest, revision, toolExecutions } = projection;
  if (
    revision.lifecycle !== 'submitted' ||
    revision.approvalRequestId !== approvalRequest.id ||
    approvalRequest.workspaceId !== revision.workspaceId ||
    approvalRequest.conversationId !== revision.conversationId
  ) {
    return { success: false, errorCode: 'ACTION_DRAFT_SCOPE_MISMATCH' };
  }
  const projectedExecutionIds = toolExecutions.map((execution) => execution.id);
  const exactExecutionLinks =
    new Set(projectedExecutionIds).size === projectedExecutionIds.length &&
    projectedExecutionIds.length === revision.toolExecutionIds.length &&
    projectedExecutionIds.every((id) => revision.toolExecutionIds.includes(id));
  if (
    toolExecutions.some(
      (execution) =>
        execution.workspaceId !== revision.workspaceId ||
        execution.conversationId !== revision.conversationId ||
        execution.approvalRequestId !== approvalRequest.id ||
        !revision.toolExecutionIds.includes(execution.id),
    ) ||
    !exactExecutionLinks
  ) {
    return { success: false, errorCode: 'ACTION_DRAFT_SCOPE_MISMATCH' };
  }
  return { success: true };
}

export function validateTelegramActionDraftCallback(
  callback: z.infer<typeof telegramActionDraftCallbackSchema>,
  expected: z.infer<typeof telegramActionDraftCallbackSchema>,
): ContractValidationResult {
  const bindingMatches =
    callback.workspaceId === expected.workspaceId &&
    callback.conversationId === expected.conversationId &&
    callback.actionDraftId === expected.actionDraftId &&
    callback.actionDraftRevisionId === expected.actionDraftRevisionId &&
    callback.approvalRequestId === expected.approvalRequestId &&
    callback.callbackId === expected.callbackId &&
    callback.idempotencyKey === expected.idempotencyKey &&
    callback.payloadFingerprint === expected.payloadFingerprint &&
    callback.transportAuthority.provider === expected.transportAuthority.provider &&
    callback.transportAuthority.chatBindingId === expected.transportAuthority.chatBindingId &&
    callback.transportAuthority.actorBindingId === expected.transportAuthority.actorBindingId &&
    callback.transportAuthority.telegramUserId === expected.transportAuthority.telegramUserId;
  return bindingMatches
    ? { success: true }
    : { success: false, errorCode: 'ACTION_DRAFT_TRANSPORT_BINDING_MISMATCH' };
}

/** Only these transaction results are permitted when revise races approval. */
export const reviseApprovalSerializationOutcomeSchema = z.enum([
  'approval_decided_before_revise',
  'pending_approval_superseded_before_submit',
  'version_conflict',
]);

export type ActionDraftAuthority = z.infer<typeof actionDraftAuthoritySchema>;
export type ActionDraft = z.infer<typeof actionDraftSchema>;
export type ActionDraftRevision = z.infer<typeof actionDraftRevisionSchema>;
export type ActionDraftCommand = z.infer<typeof actionDraftCommandSchema>;
export type ActionDraftErrorCode = z.infer<typeof actionDraftErrorCodeSchema>;
export type TelegramActionDraftCallback = z.infer<typeof telegramActionDraftCallbackSchema>;

/** S09 runtime invariant: submitting freezes the immutable revision snapshot. */
export function canMutateActionDraftRevision(
  lifecycle: z.infer<typeof actionDraftRevisionLifecycleSchema>,
): boolean {
  return lifecycle === 'editing';
}
